import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useBranding } from '@/lib/branding/BrandingProvider';

export interface NeuraxEndClient {
  id: string;
  name: string;
  email: string;
  job_title: string | null;
  target_company: string | null;
  linkedin_url: string | null;
  is_active: boolean;
  is_demo: boolean;
  created_at: string;
}

async function fetchEndClients(
  companyId: string,
  includeDemo: boolean
): Promise<NeuraxEndClient[]> {
  let q = supabase
    .from('b2b_end_clients')
    .select(
      'id, name, email, job_title, target_company, linkedin_url, is_active, is_demo, created_at'
    )
    .eq('b2b_company_id', companyId)
    .eq('is_active', true);
  if (!includeDemo) q = q.eq('is_demo', false);
  const { data, error } = await q.order('created_at', { ascending: false });

  if (error) throw new Error(`b2b_end_clients: ${error.message}`);
  return (data ?? []) as NeuraxEndClient[];
}

/**
 * `includeDemo` defaults to false so the regular interview wizard, dashboard
 * analytics, and EndClients page never see demo end_clients. The demo flow
 * opts in by passing `{ includeDemo: true }`.
 */
export function useEndClients({ includeDemo = false }: { includeDemo?: boolean } = {}) {
  const { companyId } = useBranding();
  return useQuery({
    queryKey: ['neurax', 'end-clients', companyId, { includeDemo }],
    queryFn: () => fetchEndClients(companyId, includeDemo),
    staleTime: 30_000,
  });
}
